import React, { useCallback, useEffect } from 'react';
import styles from './Modal.module.css';
import Botao from '../Botao';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import ReactDOM from 'react-dom';
import { Dialog } from '@base-ui/react/dialog';

/**
 * Modal component with support for nested dialogs.
 * 
 * This component uses Base UI's Dialog which provides automatic handling for nested modals:
 * - The CSS variable --nested-dialogs indicates the nesting depth (0 for top-level)
 * - Parent dialogs are automatically dimmed when a child dialog opens (data-nested-dialog-open)
 * - Position and scale adjust based on nesting depth for a stacked effect
 * - Backdrops of nested dialogs are automatically hidden by Base UI
 * 
 * @see https://base-ui.com/react/components/dialog.md#nested-dialogs
 */

type ModalButtonProps = {
  visible?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  href?: string;
  linkTarget?: string;
  variant?:
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-success'
  | 'outline-info'
  | 'outline-warning'
  | 'outline-danger';
};

type ModalProps = {
  closeable?: boolean;
  show: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  confirmProps?: ModalButtonProps;
  cancelProps?: ModalButtonProps;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  hideControls?: boolean;
  ignoreOverlayClick?: boolean;
  size?: 'sm' | 'md' | 'lg';
  instanceId?: string;
};

const defaultConfirmProps: ModalButtonProps = {
  visible: true,
  disabled: false,
  label: 'Confirmar',
};

const defaultCancelProps: ModalButtonProps = {
  visible: true,
  disabled: false,
  label: 'Cancelar',
};

const Modal = ({
  closeable = true,
  size = 'sm',
  show,
  title,
  children,
  confirmProps,
  cancelProps,
  hideControls,
  ignoreOverlayClick,
  instanceId,
  onClose,
  onConfirm,
  onCancel,
}: ModalProps) => {
  const confirmButtonProps = { ...defaultConfirmProps, ...confirmProps };
  const cancelButtonProps = { ...defaultCancelProps, ...cancelProps };

  const handleCancel = () => {
    onCancel ? onCancel() : onClose?.();
  };

  const handleConfirm = () => {
    onConfirm ? onConfirm() : onClose?.();
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && onClose?.()}>
      <Dialog.Portal>
        <Dialog.Backdrop
          className={styles.overlay}
          onClick={() => !ignoreOverlayClick && onClose?.()}
        />
        <Dialog.Popup
          id={instanceId}
          className={[
            styles.container,
            styles[`container-${size}`],
          ].join(' ')}
        >
          <div className={styles.titleContainer}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            {closeable && (
              <div className={styles.closeContainer}>
                <Dialog.Close
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon path={mdiClose} size={1} color="var(--tc-color-gray-500)" />
                </Dialog.Close>
              </div>
            )}
          </div>
          <div className={styles.content}>{children}</div>
          {!hideControls && (
            <div className={styles.footer}>
              {cancelButtonProps.visible && (
                <Botao
                  disabled={cancelButtonProps.disabled}
                  variant={cancelButtonProps.variant || 'outline-secondary'}
                  onClick={handleCancel}
                >
                  {cancelButtonProps.label}
                </Botao>
              )}
              {confirmButtonProps.visible && (
                <Botao
                  disabled={confirmButtonProps.disabled}
                  variant={confirmButtonProps.variant || 'primary'}
                  onClick={handleConfirm}
                  href={confirmButtonProps.href}
                  target={confirmButtonProps.linkTarget}
                >
                  {confirmButtonProps.label}
                </Botao>
              )}
            </div>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
